import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { router, usePage } from '@inertiajs/react';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import  FloatingFeedbackButton from '../../../Components/FloatingFeedbackButton.jsx';
import { calculateProfileCompleteness } from '@/Utils/profileUtils';


// Simple debounce function (no lodash needed)
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Import existing components
import SelectedScriptsList from '../SelectedScriptsList';
import ScriptSelectionModal from '../ScriptSelectionModal';
import ProfileQuestionsSection from '../ProfileQuestionsSection';

const EditProfileRedesigned = () => {
  const { profile, scripts, questions, profileQA, writerTypes, experienceLevels, availabilityOptions, urls } = usePage().props;
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [newSocialPlatform, setNewSocialPlatform] = useState('');
  const [newSocialUrl, setNewSocialUrl] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saving', 'saved', 'error'
  const [scriptsLoading, setScriptsLoading] = useState(false);

  const socialPlatforms = {
    twitter: { label: 'Twitter/X', prefix: 'https://x.com/' },
    linkedin: { label: 'LinkedIn', prefix: 'https://linkedin.com/in/' },
    instagram: { label: 'Instagram', prefix: 'https://instagram.com/' },
    youtube: { label: 'YouTube', prefix: 'https://youtube.com/' },
    personal: { label: 'Personal Website', prefix: 'https://' },
  };

  // Profile form data state
  const [formData, setFormData] = useState({
    nickname: profile.nickname || '',
    display_name: profile.display_name || '', 
    bio: profile.bio || '',
    specialty_tagline: profile.specialty_tagline || '',
    location: profile.location || '',
    availability_status: profile.availability_status || 'available',
    primary_genres: profile.primary_genres || [],
    social_links: profile.social_links || [],
    is_public: profile.is_public || false,
    allow_messages: profile.allow_messages || true,
    receive_feedback_notifications: profile.receive_feedback_notifications || true,
    writer_type: profile.writer_type || '',
    experience_level: profile.experience_level || '',
    open_to_writers_group: profile.open_to_writers_group || false,
    open_to_writing_partner: profile.open_to_writing_partner || false,
  });

  const [errors, setErrors] = useState({});

  // Calculate profile completeness using shared utility
  const calculateCompleteness = () => {
    // Filter for public scripts only, as those are the ones that count
    const publicScripts = scripts?.filter(s => s.has_profile && s.is_public) || [];
    
    // Pass the current formData state and the calculated scripts
    return calculateProfileCompleteness(formData, publicScripts);
  };

  // Add this state to track when scripts data changes
const [scriptCount, setScriptCount] = useState(0);

// Add useEffect to update script count when scripts prop changes
useEffect(() => {
  const publicScripts = scripts?.filter(s => s.has_profile && s.is_public) || [];
  setScriptCount(publicScripts.length);
}, [scripts]);

// Update the completion display to be more stable
const completionPercentage = useMemo(() => {
  return calculateCompleteness();
}, [formData, scriptCount, profileQA, questions]);


  // Auto-save function with custom debounce (no lodash)
  const autoSaveProfile = useCallback(
    debounce(async (dataToSave) => {
      setSaveStatus('saving');
      console.log('Auto-saving profile data:', dataToSave);
      
      try {
        const formDataObj = new FormData();
        Object.keys(dataToSave).forEach(key => {
          const value = dataToSave[key];
          
          if (key === 'primary_genres') {
            if (Array.isArray(value) && value.length > 0) {
              value.forEach((genre, index) => {
                formDataObj.append(`primary_genres[${index}]`, genre);
              });
            }
          } else if (key === 'social_links') {
            if (Array.isArray(value) && value.length > 0) {
              value.forEach((link, index) => {
                formDataObj.append(`social_links[${index}][platform]`, link.platform || '');
                formDataObj.append(`social_links[${index}][url]`, link.url || '');
              });
            }
          } else if (typeof value === 'boolean') {
            formDataObj.append(key, value ? '1' : '0');
          } else if (value !== null && value !== undefined && value !== '') {
            formDataObj.append(key, value);
          }
        });
        formDataObj.append('_method', 'PUT');

        const response = await fetch(urls.update, {
          method: 'POST',
          body: formDataObj,
          headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
          }
        });

        if (response.ok) {
          setSaveStatus('saved');
          console.log('Auto-save successful');
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Auto-save failed:', response.status, errorData);
          throw new Error(errorData.message || 'Save failed');
        }
      } catch (error) {
        setSaveStatus('error');
        console.error('Auto-save error:', error);
        toast.error('Failed to save profile changes: ' + error.message);
      }
    }, 1000),
    [urls.update]
  );

  // Handle form field changes with auto-save
  const handleFieldChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    autoSaveProfile(newData);
  };

  const addGenre = (genre) => {
    if (genre.trim() && formData.primary_genres.length < 3 && !formData.primary_genres.includes(genre.trim())) {
      const newGenres = [...formData.primary_genres, genre.trim()];
      const newData = { ...formData, primary_genres: newGenres };
      setFormData(newData);
      autoSaveProfile(newData);
    }
  };

  const removeGenre = (indexToRemove) => {
    const newGenres = formData.primary_genres.filter((_, index) => index !== indexToRemove);
    const newData = { ...formData, primary_genres: newGenres };
    setFormData(newData);
    autoSaveProfile(newData);
  };

  const addSocialLink = () => {
    if (newSocialPlatform && newSocialUrl.trim()) {
      const newLink = { platform: newSocialPlatform, url: newSocialUrl.trim() };
      const newLinks = [...formData.social_links, newLink];
      const newData = { ...formData, social_links: newLinks };
      setFormData(newData);
      autoSaveProfile(newData);
      setNewSocialPlatform('');
      setNewSocialUrl('');
    }
  };

  const removeSocialLink = (index) => {
    const newLinks = formData.social_links.filter((_, i) => i !== index);
    const newData = { ...formData, social_links: newLinks };
    setFormData(newData);
    autoSaveProfile(newData);
  };

  // Handle profile photo upload
// Handle profile photo upload
const handleProfilePhotoChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    // Validate file size (2MB max)
    if (file.size > 2048 * 1024) {
      toast.error('Image must be smaller than 2MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setSaveStatus('saving');
    
    // Create FormData with ALL current form data to prevent data loss
    const formDataObj = new FormData();
    formDataObj.append('profile_photo', file);
    
    // Include ALL form data to prevent other fields from being lost
    Object.keys(formData).forEach(key => {
      const value = formData[key];
      
      if (key === 'primary_genres') {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((genre, index) => {
            formDataObj.append(`primary_genres[${index}]`, genre);
          });
        }
      } else if (key === 'social_links') {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((link, index) => {
            formDataObj.append(`social_links[${index}][platform]`, link.platform || '');
            formDataObj.append(`social_links[${index}][url]`, link.url || '');
          });
        }
      } else if (typeof value === 'boolean') {
        formDataObj.append(key, value ? '1' : '0');
      } else if (value !== null && value !== undefined && value !== '') {
        formDataObj.append(key, value);
      }
    });
    
    formDataObj.append('_method', 'PUT');

    fetch(urls.update, {
      method: 'POST',
      body: formDataObj,
      headers: {
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
      }
    })
    .then(async response => {
      if (response.ok) {
        setSaveStatus('saved');
        toast.success('Profile photo updated!');
        setTimeout(() => {
          router.reload({ only: ['profile'] });
        }, 1000);
      } else {
        const errorData = await response.json();
        console.error('Upload failed:', errorData);
        
        if (errorData.errors) {
          Object.entries(errorData.errors).forEach(([field, messages]) => {
            console.error(`Validation error for ${field}:`, messages);
            toast.error(`${field}: ${messages[0]}`);
          });
        }
        
        throw new Error(errorData.message || 'Upload failed');
      }
    })
    .catch(error => {
      setSaveStatus('error');
      console.error('Upload error:', error);
      toast.error('Failed to upload photo: ' + error.message);
    });
  }
};

  const goToDashboard = () => {
    router.visit('/dashboard');
  };

  // Status indicator component
  const SaveStatusIndicator = () => {
    switch (saveStatus) {
      case 'saving':
        return (
          <div className="text-warning small d-flex align-items-center">
            <div className="spinner-border spinner-border-sm me-2" role="status" style={{width: '12px', height: '12px'}}>
              <span className="visually-hidden">Saving...</span>
            </div>
            Saving changes...
          </div>
        );
      case 'saved':
        return (
          <div className="text-success small d-flex align-items-center">
            <i className="fa fa-check-circle me-2"></i>
            All changes saved
          </div>
        );
      case 'error':
        return (
          <div className="text-danger small d-flex align-items-center">
            <i className="fa fa-exclamation-triangle me-2"></i>
            Save failed
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-vh-100 bg-light">
      <div className="container py-4">
        <div className="row justify-content-center">
          <div className="col-lg-10">
            <div className="card shadow-lg border-0">
              {/* Header */}
              <div className="card-header text-white py-5 border-0" 
                   style={{background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)'}}>
                <h2 className="display-6 fw-semibold mb-2">Edit Your Profile</h2>
                <p className="lead opacity-90 mb-0">Changes save automatically as you type</p>
              </div>

              <div className="card-body p-5">

                {/* Visibility & URL Section */}
<div className="mb-5">
  <h3 className="h4 fw-semibold mb-4 text-dark d-flex align-items-center">
    <span className="me-3">🔗</span>
    Visibility & Portfolio URL
  </h3>

  <div className="bg-light border border-info rounded p-4 mb-4 text-dark">
    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
      <div>
        <h5 className="fw-semibold mb-1">Portfolio Visibility</h5>
        <p className="text-muted small mb-0">Control who can see your writer profile</p>
      </div>
      <div className="d-flex align-items-center gap-3">
        <span className={`small fw-medium ${formData.is_public ? 'text-muted' : 'text-dark'}`}>
          Private
        </span>
        <div className="form-check form-switch">
          <input
            type="checkbox"
            checked={formData.is_public}
            onChange={(e) => handleFieldChange('is_public', e.target.checked)}
            className="form-check-input"
            style={{ fontSize: '1.2rem' }}
          />
        </div>
        <span className={`small fw-medium ${formData.is_public ? 'text-dark' : 'text-muted'}`}>
          Public
        </span>
      </div>
    </div>

    {formData.is_public && formData.nickname && (
      <div className="border-top pt-3 mt-3">
        <p className="small text-muted mb-2">Your portfolio is live at:</p>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="text-dark small font-monospace pe-3">
            <span className="text-muted me-1">URL:</span>
            <a
              href={`/portfolios/${formData.nickname}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none text-dark"
            >
              scriptreader.ai/portfolios/{formData.nickname}
            </a>
          </div>

          <a
            href={`/portfolios/${formData.nickname}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-primary btn-sm"
          >
            <i className="fa fa-external-link-alt me-1"></i>
            View
          </a>
        </div>
      </div>
    )}

    {!formData.is_public && (
      <div className="border-top pt-3 mt-3">
        <div className="d-flex align-items-center gap-2 text-muted small">
          <i className="fa fa-eye-slash"></i>
          Your portfolio is private and not visible to others.
        </div>
      </div>
    )}
  </div>
</div>

                {/* Basic Information Section */}
                <div className="mb-5">
                  <h3 className="h4 fw-semibold mb-4 text-dark d-flex align-items-center">
                    <span className="me-3">👤</span>
                    Basic Information
                  </h3>

                  <div className="row g-4">
                    {/* Profile Photo */}
                    <div className="col-12">
                      <label className="form-label fw-semibold text-dark">Profile Photo</label>
                      <div className="d-flex align-items-center gap-3">
                        <div className="rounded-circle bg-light d-flex align-items-center justify-content-center border"
                             style={{width: '64px', height: '64px', overflow: 'hidden'}}>
                          {profile.profile_photo_url ? (
                            <img 
                              src={profile.profile_photo_url} 
                              alt="Profile" 
                              className="w-100 h-100"
                              style={{objectFit: 'cover'}}
                            />
                          ) : (
                            <i className="fa fa-user text-muted"></i>
                          )}
                        </div>
                        <div>
                          <label className="btn btn-primary btn-sm">
                            Upload Photo
                            <input
                              type="file"
                              className="d-none"
                              accept="image/*"
                              onChange={handleProfilePhotoChange}
                            />
                          </label>
                          <div className="form-text">Uploads automatically</div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-dark">
                        Display name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.display_name}
                        onChange={(e) => handleFieldChange('display_name', e.target.value)}
                        placeholder="Your professional name"
                        className="form-control form-control-lg"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-dark">
                        Portfolio URL <span className="text-danger">*</span>
                      </label>
                      <div className="input-group input-group-lg">
                        <span className="input-group-text bg-light text-muted">
                          scriptreader.ai/portfolios/
                        </span>
                        <input
                          type="text"
                          value={formData.nickname}
                          onChange={(e) => handleFieldChange('nickname', e.target.value)}
                          placeholder="your-name"
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold text-dark">
                        Specialty tagline
                      </label>
                      <input
                        type="text"
                        value={formData.specialty_tagline}
                        onChange={(e) => handleFieldChange('specialty_tagline', e.target.value)}
                        placeholder="e.g., Character-driven sci-fi thrillers"
                        className="form-control form-control-lg"
                        maxLength="100"
                      />
                      <div className="form-text d-flex justify-content-between">
                        <span>One line describing your specialty</span>
                        <span className="text-muted">{100 - (formData.specialty_tagline?.length || 0)}</span>
                      </div>
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold text-dark">Bio</label>
                      <textarea
                        rows="4"
                        value={formData.bio}
                        onChange={(e) => handleFieldChange('bio', e.target.value)}
                        placeholder="Tell visitors about your writing background, what drives your storytelling, and what makes your scripts unique..."
                        className="form-control"
                        style={{resize: 'vertical'}}
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-dark d-flex align-items-center">
                        <i className="fa fa-map-marker-alt me-2"></i>
                        Location
                      </label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => handleFieldChange('location', e.target.value)}
                        placeholder="e.g., Los Angeles, CA"
                        className="form-control form-control-lg"
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-dark d-flex align-items-center">
                        <i className="fa fa-clock me-2"></i>
                        Availability
                      </label>
                      <select
                        value={formData.availability_status}
                        onChange={(e) => handleFieldChange('availability_status', e.target.value)}
                        className="form-select form-select-lg"
                      >
                        {Object.entries(availabilityOptions || {}).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold text-dark d-flex align-items-center">
                        <i className="fa fa-tags me-2"></i>
                        Primary genres <span className="text-muted small">(max 3)</span>
                      </label>
                      
                      <div className="border rounded p-3 bg-light" style={{minHeight: '60px'}}>
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {formData.primary_genres.map((genre, index) => (
                            <span
                              key={index}
                              className="badge bg-primary d-flex align-items-center"
                              style={{fontSize: '0.9rem'}}
                            >
                              {genre}
                              <button
                                type="button"
                                onClick={() => removeGenre(index)}
                                className="btn-close btn-close-white btn-sm ms-2"
                                style={{fontSize: '0.6rem'}}
                              ></button>
                            </span>
                          ))}
                        </div>
                        
                        {formData.primary_genres.length < 3 && (
                          <input
                            type="text"
                            placeholder="Add a genre and press Enter..."
                            onKeyDown={(e) => {
                              if ((e.key === 'Enter' || e.key === ',') && e.target.value.trim()) {
                                e.preventDefault();
                                addGenre(e.target.value);
                                e.target.value = '';
                              }
                            }}
                            className="form-control form-control-sm border-0 bg-transparent"
                            style={{outline: 'none'}}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scripts Section */}
              <div className="px-5 mb-5">
                <h3 className="h4 fw-semibold mb-4 text-dark d-flex align-items-center">
                  <span className="me-3">📄</span>
                  Your Scripts
                  <small className="text-muted ms-3">(changes save automatically)</small>
                  {scriptsLoading && (
                    <div className="spinner-border spinner-border-sm ms-3 text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  )}
                </h3>
                <SelectedScriptsList 
                  key={`scripts-${refreshKey}-${scripts?.length || 0}`}
                  onOpenModal={() => setIsModalOpen(true)} 
                />
              </div>

              {/* About Me Questions */}
              <div className="px-5 mb-5">
                <h3 className="h4 fw-semibold mb-4 text-dark d-flex align-items-center">
                  <span className="me-3">🎭</span>
                  About Me / Lightning Round
                  <small className="text-muted ms-3">(auto-saves as you type)</small>
                </h3>
                <ProfileQuestionsSection autoSave={true} />
              </div>


              {/* Social Links Section */}
              <div className="px-5 mb-5">
                <h3 className="h4 fw-semibold mb-4 text-dark d-flex align-items-center">
                  <span className="me-3">🔗</span>
                  Social Links
                  <small className="text-muted ms-3">(saves automatically)</small>
                </h3>

                <div className="row g-4">
                  <div className="col-12">
                    <label className="form-label fw-semibold text-dark">Social Links</label>
                    
                    {formData.social_links.length > 0 && (
                      <div className="mb-3">
                        {formData.social_links.map((link, index) => (
                          <div key={index} className="d-flex align-items-center gap-3 p-3 bg-light rounded mb-2">
                            <span className="badge bg-secondary" style={{minWidth: '80px'}}>
                              {socialPlatforms[link.platform]?.label || 'Custom'}
                            </span>
                            <span className="flex-grow-1 font-monospace small text-muted">
                              {socialPlatforms[link.platform]?.prefix}{link.url}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeSocialLink(index)}
                              className="btn btn-sm btn-outline-danger"
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="card bg-light border-secondary">
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-4">
                            <select 
                              className="form-select form-select-sm"
                              value={newSocialPlatform}
                              onChange={(e) => setNewSocialPlatform(e.target.value)}
                            >
                              <option value="">Choose platform...</option>
                              {Object.entries(socialPlatforms).map(([key, val]) => (
                                <option key={key} value={key}>{val.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6">
                            <input
                              type="text"
                              placeholder="username or URL"
                              value={newSocialUrl}
                              onChange={(e) => setNewSocialUrl(e.target.value)}
                              className="form-control form-control-sm"
                            />
                          </div>
                          <div className="col-md-2">
                            <button 
                              type="button"
                              onClick={addSocialLink}
                              disabled={!newSocialPlatform || !newSocialUrl.trim()}
                              className="btn btn-primary btn-sm w-100"
                            >
                              <i className="fa fa-plus me-1"></i>
                              Add
                            </button>
                          </div>
                        </div>
                        <div className="form-text">Social links save automatically when added</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-5 mb-4">

                {/* Collaboration Survey - Very Top */}
                <div className="mb-5">
                  <h3 className="h4 fw-semibold mb-4 text-dark d-flex align-items-center">
                    <span className="me-3">🤝</span>
                    Community & Collaboration
                  </h3>

<div className="alert border-0 mb-4" style={{ background: '#f8f9fa', borderLeft: '4px solid #0dcaf0' }}>
  <p className="mb-0 small text-dark">
    <i className="fa fa-info-circle me-1 text-info"></i>
                      These features aren't implemented yet, but your input helps us prioritize development.
                    </p>
                    
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="form-check form-switch">
                          <input
                            type="checkbox"
                            checked={formData.open_to_writers_group}
                            onChange={(e) => handleFieldChange('open_to_writers_group', e.target.checked)}
                            className="form-check-input"
                            id="writersGroup"
                          />
                          <label className="form-check-label" htmlFor="writersGroup">
                            <div className="fw-semibold text-dark">Interested in joining writer's groups</div>
                            <div className="small text-muted">                                Connect with other writers on ScriptReader.ai for feedback and support.
</div>
                          </label>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="form-check form-switch">
                          <input
                            type="checkbox"
                            checked={formData.open_to_writing_partner}
                            onChange={(e) => handleFieldChange('open_to_writing_partner', e.target.checked)}
                            className="form-check-input"
                            id="writingPartner"
                          />
                          <label className="form-check-label" htmlFor="writingPartner">
                            <div className="fw-semibold text-dark">Interested in finding writing partners</div>
                            <div className="small text-muted">                                We will help you find a writing partner to collaborate with based on interests, strengths and weaknesses, writing process compatibility, and more.  Not implemented yet.
</div>
                          </label>
                        </div>
                      </div>
                    </div>
                    
                  </div>
                </div>
                </div>

              {/* Completion Status */}
              <div className="px-5 mb-4">
                <div className="alert alert-info border-0" style={{backgroundColor: '#EBF8FF'}}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small fw-medium text-info">Profile completeness:</span>
                    <span className="fw-semibold text-dark">{calculateCompleteness()}%</span>
                  </div>
                  <div className="progress mb-2" style={{height: '8px'}}>
                    <div 
                      className="progress-bar bg-info"
                      style={{width: `${calculateCompleteness()}%`}}
                    ></div>
                  </div>
                  <p className="small text-info mb-0">
                    Complete your profile to help others discover your work!
                  </p>
                </div>
              </div>

              {/* Navigation */}
              <div className="card-footer bg-light border-top d-flex justify-content-between align-items-center py-4">
                <div className="d-flex align-items-center gap-4">
                  <div className="text-muted small">
                    Profile: {calculateCompleteness()}% complete
                  </div>
                  <SaveStatusIndicator />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Script Selection Modal */}
      <ScriptSelectionModal 
        isOpen={isModalOpen} 
        onClose={(scriptsWereAdded) => {
          setIsModalOpen(false);
          
          // Only reload if scripts were actually added
          if (scriptsWereAdded) {
            setScriptsLoading(true);
            console.log('Scripts were added, refreshing list...');
            
            // Force refresh of scripts data
            setTimeout(() => {
         router.visit(window.location.pathname, {
          only: ['scripts'],
          preserveState: true,
          preserveScroll: true,
          replace: true, // Don't add to browser history
          onSuccess: () => {
            console.log('Scripts list refreshed successfully');
            setRefreshKey(prev => prev + 1);
            setScriptsLoading(false);
            toast.success('Scripts updated!');
          },

                onError: (errors) => {
                  console.error('Failed to refresh scripts:', errors);
                  setScriptsLoading(false);
                  toast.error('Failed to refresh scripts list');
                }
              });
            }, 500); // Give server time to process
          }
        }}
      />

      <ToastContainer position="top-right" autoClose={3000} />
      <FloatingFeedbackButton />
    </div>
  );
};

export default EditProfileRedesigned;
